import {
  GoogleAdsCampaign,
  MetaCampaign,
  PaidSearchEfficiency,
  PaidSocialEfficiency,
  SourceLeadsBreakdown,
  SourceRow,
  ViewMode
} from "@/lib/types";

/**
 * Selectors that resolve a dashboard field against the active view mode.
 *
 * The monthly payload is the default shape. When the header toggle is set to
 * "current_week", the same numbers come from `payload.weekly_reporting`, which
 * the pipeline builds separately (see `weekly_reporting` in
 * `src/google_sheets_reporter.py`). The two shapes differ, so every weekly read
 * goes through here rather than being inlined in a page.
 */

const EMPTY_TOTALS = { mql: 0, sql: 0, demo_dones: 0, sqo: 0, wins: 0, revenue: 0 };

function weekly(payload: any) {
  return payload?.weekly_reporting || {};
}

function isWeek(viewMode: ViewMode) {
  return viewMode === "current_week";
}

/**
 * Weekly summary rows arrive straight from the pandas funnel builder, so they
 * carry capitalised column names and a trailing "Total" row. Monthly rows are
 * already lower-cased by the reporter, so normalise the weekly ones to match.
 */
function normalizeWeeklyRow(row: any): SourceRow {
  return {
    source: row?.Source,
    mql: row?.MQL || 0,
    sql: row?.SQL || 0,
    demoDones: row?.["Demo Done"] || 0,
    sqo: row?.SQO || 0,
    wins: row?.Wins || 0,
    revenue: row?.Revenue || 0
  };
}

export function getTotals(payload: any, viewMode: ViewMode) {
  if (!isWeek(viewMode)) return payload?.totals || EMPTY_TOTALS;

  const totalRow = (weekly(payload).summary || []).find(
    (row: any) => row?.Source === "Total"
  );
  if (!totalRow) return EMPTY_TOTALS;

  const { source: _source, ...totals } = normalizeWeeklyRow(totalRow);
  return totals;
}

export function getSourceSummary(payload: any, viewMode: ViewMode): SourceRow[] {
  if (!isWeek(viewMode)) return payload?.source_summary || [];

  return (weekly(payload).summary || [])
    .filter((row: any) => row?.Source !== "Total")
    .map(normalizeWeeklyRow);
}

export function getPaidSocialEfficiency(
  payload: any,
  viewMode: ViewMode
): PaidSocialEfficiency {
  if (!isWeek(viewMode)) return payload?.paid_social_efficiency || {};
  return weekly(payload).paid_social_weekly_efficiency || {};
}

export function getPaidSearchEfficiency(
  payload: any,
  viewMode: ViewMode
): PaidSearchEfficiency {
  if (!isWeek(viewMode)) return payload?.paid_search_efficiency || {};
  return weekly(payload).paid_search_weekly_efficiency || {};
}

/**
 * The weekly payload stores only aggregate ad summaries — the pipeline never
 * persists per-campaign rows for a week — so the campaign tables have nothing
 * to show in week mode.
 */
export function getMetaCampaigns(payload: any, viewMode: ViewMode): MetaCampaign[] {
  if (isWeek(viewMode)) return [];
  return payload?.meta_campaigns || [];
}

export function getGoogleAdsCampaigns(
  payload: any,
  viewMode: ViewMode
): GoogleAdsCampaign[] {
  if (isWeek(viewMode)) return [];
  return payload?.google_ads_campaigns || [];
}

/**
 * There is no weekly lead breakdown in the payload, so week mode keeps showing
 * the month's leads rather than blanking the tables out.
 */
export function getFilteredBreakdown(
  payload: any,
  _viewMode: ViewMode
): SourceLeadsBreakdown {
  const breakdown: SourceLeadsBreakdown = payload?.source_leads_breakdown || {};
  const EXCLUDED_DRILLDOWN_SOURCES = new Set([
    "Sales Initiative",
    "Hudson's Grill Nearby Campaign",
    "Tulsa Zoo - Nearby Campaign",
    "Talsa Zoo - Nearby Campaign",
    "Square POS Outbound Campaign"
  ]);

  const filtered: SourceLeadsBreakdown = {};
  for (const [source, list] of Object.entries(breakdown)) {
    if (source.includes("AI Website Builder")) continue;
    if (EXCLUDED_DRILLDOWN_SOURCES.has(source.trim())) continue;
    filtered[source] = list;
  }
  return filtered;
}
